/* @flow */

import React, { useContext, useEffect, useState } from 'react';
import loadable from '@loadable/component';

import { useDispatch, useSelector, useStore } from 'react-redux';
import Breadcrumbs from '../../components/Breadcrumbs';
import Search from '../../components/Search';
import TilesVideo from '../../components/TilesVideo';
import Button from '../../components/Button2.0';
import Preloader from '../../components/Preloader';

import { generateBreadcrumbs } from '../../utils/breadcrumbs';
import { Buttons, ExtraButtonWrapper, Header, List, Root, SearchWrapper, Title } from '../knowledge/styles';
import { Container } from '../../components/Layout/styles';
import type { TRootState } from '../../types/rootState';
import type { TCity } from '../../types/city';
import type { TVideoCategory, TVideoState } from '../../types/video';
import { getVideoContent, searchVideoContent } from '../../actions/video';
import { AppContext } from '../../components/AppContext';
import type { TDispatch } from '../../types/core';
import { CLEAR_VIDEO_STATE } from '../../constants/video';

const PopupVideo = loadable(() => import('../../components/PopupVideo'), { fallback: <Preloader /> });

const searchData = {
	type: 'text',
	placeholder: 'Введите название ЖК',
};

const Video = () => {
	const dispatch: TDispatch = useDispatch();
	const store = useStore();
	const context = useContext(AppContext);
	const video: TVideoState = useSelector((state: TRootState) => state.video);
	const city: TCity = useSelector((state: TRootState) => state.city);

	const { videos, categories, categorySlug, pageNumber, pageCount, isLoading } = video;

	const [videoPopup, setVideoPopup] = useState('');
	const [searchValue, setSearchValue] = useState('');

	const breadCrumbsNew = generateBreadcrumbs(city.link, [
		{
			index: 3,
			title: 'Видео',
			link: `/video/${city.link}/`,
		},
	]);

	const loadMore = async () => getVideoContent(pageNumber + 1, categorySlug)(dispatch, store.getState, context);

	const handleChangeSearch = async (value) => {
		setSearchValue(value);

		if (value.length > 1) {
			await searchVideoContent(value)(dispatch, store.getState, context);
		}
	};

	const handleOpenPopup = (code) => () => {
		setVideoPopup(code);
	};

	const handleClosePopup = () => {
		setVideoPopup('');
	};

	useEffect(() => {
		if (categories?.length === 0 && searchValue?.length === 0 && !isLoading) {
			dispatch({ type: CLEAR_VIDEO_STATE });
			getVideoContent(0, categorySlug)(dispatch, store.getState, context);
		}
	}, [searchValue]);

	useEffect(() => {
		if (videos?.length === 0 && !isLoading) {
			getVideoContent(0, categorySlug)(dispatch, store.getState, context);
		}
	}, []);

	return (
		<Root>
			<Container>
				<Breadcrumbs data={breadCrumbsNew} />

				<Header>
					<Title>Видео обзоры о недвижимости</Title>
					<SearchWrapper>
						<Search data={searchData} value={searchValue} handleChangeSearch={handleChangeSearch} />
					</SearchWrapper>
				</Header>
				{searchValue?.length === 0 && (
					<Buttons>
						{categories?.map((item: TVideoCategory) => (
							<Button
								isLink
								to={
									categorySlug === item.slug
										? `/video/${city.link}/`
										: `/video/${city.link}/${item.slug}`
								}
								key={item.id}
								size="xs"
								color={categorySlug === item.slug ? 'primary' : 'secondary'}
							>
								{item.title}
							</Button>
						))}
					</Buttons>
				)}

				{videos?.length > 0 && (
					<List>
						<TilesVideo data={videos} handleOpenPopup={handleOpenPopup} />
						{isLoading && <Preloader />}
						{pageNumber < pageCount - 1 && (
							<ExtraButtonWrapper>
								<Button onClick={loadMore} size="s" color="gray" style={{ padding: '0 30px' }}>
									Показать ещё
								</Button>
							</ExtraButtonWrapper>
						)}
					</List>
				)}
			</Container>

			<PopupVideo visible={!!videoPopup} handleClosePopup={handleClosePopup} id={videoPopup} />
		</Root>
	);
};

export default Video;
